import { useCallback, useState } from 'react'
import type { ServiceCardState, AddonState, CatalogData } from '../types'

function buildDefaultCard(): ServiceCardState {
  return {
    id: crypto.randomUUID(),
    selectedServiceKey: '',
    selectedOfferingId: '',
    date: '',
    time: '',
    location: '',
    hours: 1,
    filmLengthMinutes: 1,
    headshots: 1,
    complexity: 'simple',
    includeAudioRecording: false,
    addons: [],
  }
}

function buildAddonsFromOffering(
  offeringId: string,
  serviceKey: string,
  catalog: CatalogData
): AddonState[] {
  const offering = catalog.offerings.find((o) => o.id === offeringId)
  const assignedAddonIds = new Set(offering?.addonIds ?? [])

  return catalog.addons
    .filter(
      (a) =>
        assignedAddonIds.has(a.id) ||
        a.appliesToCategories.includes(serviceKey) ||
        a.appliesToOfferings.includes(offeringId)
    )
    .map((a) => ({
      addonId: a.id,
      label: a.label,
      selectionMode: a.selectionMode,
      pricingMode: a.pricingMode,
      rate: a.rate,
      maxQty: a.maxQty,
      needsHoursForPerMinute: a.needsHoursForPerMinute,
      boolValue: false,
      intValue: 0,
      stringValue: '',
    }))
}

interface UseServiceCardsResult {
  cards: ServiceCardState[]
  addCard: () => void
  removeCard: (id: string) => void
  updateCard: (id: string, patch: Partial<ServiceCardState>) => void
  updateCardService: (id: string, serviceKey: string) => void
  updateCardOffering: (id: string, offeringId: string, catalog: CatalogData) => void
  prefillCard: (id: string, serviceKey: string, offeringId: string, catalog: CatalogData) => void
  updateAddon: (cardId: string, addonId: string, patch: Partial<AddonState>) => void
}

export function useServiceCards(): UseServiceCardsResult {
  const [cards, setCards] = useState<ServiceCardState[]>([buildDefaultCard()])

  const addCard = useCallback(() => {
    setCards((prev) => [...prev, buildDefaultCard()])
  }, [])

  const removeCard = useCallback((id: string) => {
    setCards((prev) => {
      if (prev.length <= 1) return prev
      return prev.filter((c) => c.id !== id)
    })
  }, [])

  const updateCard = useCallback((id: string, patch: Partial<ServiceCardState>) => {
    setCards((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)))
  }, [])

  const updateCardService = useCallback((id: string, serviceKey: string) => {
    // Radix Select's hidden native <select> can fire onChange with "" right
    // after we set the value externally (race between programmatic value
    // update and item-list mount). The placeholder isn't a user-selectable
    // value, so an empty serviceKey here is always spurious — ignore it.
    if (!serviceKey) return
    setCards((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, selectedServiceKey: serviceKey, selectedOfferingId: '', addons: [] }
          : c
      )
    )
  }, [])

  const updateCardOffering = useCallback(
    (id: string, offeringId: string, catalog: CatalogData) => {
      // Same Radix-native-select guard as updateCardService.
      if (!offeringId) return
      setCards((prev) => {
        const card = prev.find((c) => c.id === id)
        if (!card) return prev
        const addons = buildAddonsFromOffering(offeringId, card.selectedServiceKey, catalog)
        return prev.map((c) =>
          c.id === id
            ? {
                ...c,
                selectedOfferingId: offeringId,
                addons,
                // Reset complexity when offering changes
                complexity: 'simple' as const,
                includeAudioRecording: false,
              }
            : c
        )
      })
    },
    []
  )

  const prefillCard = useCallback(
    (id: string, serviceKey: string, offeringId: string, catalog: CatalogData) => {
      const addons = buildAddonsFromOffering(offeringId, serviceKey, catalog)
      setCards((prev) =>
        prev.map((c) =>
          c.id === id
            ? {
                ...c,
                selectedServiceKey: serviceKey,
                selectedOfferingId: offeringId,
                addons,
                complexity: 'simple' as const,
                includeAudioRecording: false,
              }
            : c
        )
      )
    },
    []
  )

  const updateAddon = useCallback(
    (cardId: string, addonId: string, patch: Partial<AddonState>) => {
      setCards((prev) =>
        prev.map((c) =>
          c.id === cardId
            ? {
                ...c,
                addons: c.addons.map((a) =>
                  a.addonId === addonId ? { ...a, ...patch } : a
                ),
              }
            : c
        )
      )
    },
    []
  )

  return { cards, addCard, removeCard, updateCard, updateCardService, updateCardOffering, prefillCard, updateAddon }
}
